import React, { useState, useEffect, useCallback } from "react";
import { Badge, Button, Tabs, Tab, Row, Col, Card, Form } from "react-bootstrap";
import { FaSync, FaHistory, FaInfoCircle, FaChartBar, FaTools } from "react-icons/fa";
import DataTable from "../components/ui/DataTable";
import { getActivityLogs, clearOldLogs, getDashboardStats, getHealthStatus, getSettings, updateSettings } from "../api/services/AdminService";

// Refactored Modular Components
import DashboardMetrics from "../components/admin/DashboardMetrics";
import TrendAnalysis from "../components/admin/TrendAnalysis";
import FinancialMetrics from "../components/admin/FinancialMetrics";
import RecentPayments from "../components/admin/RecentPayments";
import HealthMonitor from "../components/admin/HealthMonitor";
import LogFilters from "../components/admin/LogFilters";
import LogDetailsModal from "../components/admin/LogDetailsModal";
import PaymentRecovery from "../components/admin/PaymentRecovery";

const AdminLogs = () => {
    const [activeTab, setActiveTab] = useState("dashboard");
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        traffic: { totalRequests: 0, avgLatency: 0, errors: 0, successes: 0, errorRate: 0 },
        applications: { totalApplications: 0, paid: 0, pending: 0, failed: 0, trends: { today: 0, yesterday: 0, thisWeek: 0, thisMonth: 0, lastMonth: 0 } },
        payments: { totalAmount: 0, successfulPayments: 0, failedPayments: 0, pendingPayments: 0, recentPending: [] }
    });
    const [health, setHealth] = useState({ backend: 'Checking...', database: 'Checking...', cashfree: 'Checking...', uptime: 0 });
    const [settings, setSettings] = useState({ isMaintenanceMode: false });
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
    const [filters, setFilters] = useState({ url: "", status: "", method: "" });
    const [selectedLog, setSelectedLog] = useState(null);

    const fetchData = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            const [logsRes, statsRes, healthRes, settingsRes] = await Promise.all([
                getActivityLogs({ ...filters, page, limit: 10 }),
                getDashboardStats(),
                getHealthStatus(),
                getSettings()
            ]);

            setLogs(logsRes.data || []);
            setPagination(logsRes.pagination);
            setStats(statsRes.data);
            setHealth(healthRes.data);
            if (settingsRes.data) {
                setSettings(settingsRes.data);
            }
        } catch (err) {
            console.error("Error fetching data:", err);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleClearLogs = async () => {
        if (!window.confirm("Are you sure you want to clear logs older than 30 days?")) return;
        try {
            await clearOldLogs(30);
            alert("Old logs cleared successfully");
            fetchData();
        } catch (err) {
            alert("Failed to clear logs");
        }
    };

    const handleToggleMaintenance = async () => {
        const newStatus = !settings.isMaintenanceMode;
        if (!window.confirm(`Are you sure you want to turn ${newStatus ? 'ON' : 'OFF'} Maintenance Mode?`)) return;

        try {
            await updateSettings({ isMaintenanceMode: newStatus });
            setSettings(prev => ({ ...prev, isMaintenanceMode: newStatus }));
            alert(`Maintenance mode turned ${newStatus ? 'ON' : 'OFF'}`);
        } catch (err) {
            alert("Failed to update settings");
        }
    };

    const getStatusBadge = (status) => {
        if (status < 300) return <Badge bg="success">{status}</Badge>;
        if (status < 400) return <Badge bg="info">{status}</Badge>;
        if (status < 500) return <Badge bg="warning text-dark">{status}</Badge>;
        return <Badge bg="danger">{status}</Badge>;
    };

    const columns = [
        { key: "createdAt", label: "Time", render: (val) => new Date(val).toLocaleString() },
        { key: "method", label: "Method", render: (val) => <Badge bg="dark">{val}</Badge> },
        { key: "url", label: "URL" },
        { key: "status", label: "Status", render: (val) => getStatusBadge(val) },
        { key: "duration", label: "Latency", render: (val) => <span>{val}ms</span> },
        { key: "ipAddress", label: "IP" }
    ];

    const actions = [
        {
            label: "Details",
            icon: <FaInfoCircle />,
            variant: "outline-info",
            onClick: (item) => setSelectedLog(item),
            tooltip: "View full request/response details"
        }
    ];

    return (
        <div className="superadmin-center">
            <header className="content-header d-flex justify-content-between align-items-center mb-4">
                <h1 className="mb-0 d-flex align-items-center">
                    <FaHistory className="me-2 text-primary" /> Superadmin Center
                </h1>

                <div className="d-flex gap-2 align-items-center">
                    <Form.Check
                        type="switch"
                        id="maintenance-switch"
                        label={<span className={settings.isMaintenanceMode ? "text-danger fw-bold" : "text-muted"}>Maintenance Mode</span>}
                        checked={settings.isMaintenanceMode}
                        onChange={handleToggleMaintenance}
                        className="me-3"
                    />
                    <Button variant="outline-primary" onClick={() => fetchData(pagination.page)}>
                        <FaSync className="me-2" /> Refresh Data
                    </Button>
                </div>
            </header>

            <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-4 custom-tabs"
            >
                {/* DASHBOARD TAB */}
                <Tab eventKey="dashboard" title={<span><FaChartBar className="me-2" /> Dashboard</span>}>
                    <DashboardMetrics stats={stats} />

                    <Row className="g-4">
                        <Col md={12}>
                            <RecentPayments payments={stats.payments} />
                        </Col>
                    </Row>

                    <TrendAnalysis trends={stats.applications.trends} />
                    <FinancialMetrics stats={stats} />
                    <HealthMonitor health={health} />
                </Tab>

                {/* ACTIVITY LOGS TAB */}
                <Tab eventKey="logs" title={<span><FaHistory className="me-2" /> Activity Logs</span>}>
                    <LogFilters
                        filters={filters}
                        setFilters={setFilters}
                        onFilter={() => fetchData(1)}
                        onClearOldLogs={handleClearLogs}
                    />

                    <DataTable
                        columns={columns}
                        data={logs}
                        actions={actions}
                        pagination={true}
                        serverSide={true}
                        totalItems={pagination.total}
                        totalPages={pagination.pages}
                        page={pagination.page}
                        pageSize={pagination.limit}
                        onPageChange={(p) => fetchData(p)}
                        loading={loading}
                        emptyMessage="No activity logs found matching the filters."
                    />
                </Tab>

                {/* PAYMENT RECOVERY TAB */}
                <Tab eventKey="recovery" title={<span><FaSync className="me-2" /> Payment Recovery</span>}>
                    <PaymentRecovery />
                </Tab>
            </Tabs>

            <LogDetailsModal
                log={selectedLog}
                show={!!selectedLog}
                onHide={() => setSelectedLog(null)}
                getStatusBadge={getStatusBadge}
            />

            <style>{`
                .letter-spacing-1 { letter-spacing: 1px; }
                .custom-tabs .nav-link { color: #6c757d; border: none; font-weight: 500; padding: 1rem 1.5rem; }
                .custom-tabs .nav-link.active { color: var(--primary-color); border-bottom: 3px solid var(--primary-color); background: transparent; }
                .superadmin-center .content-header h1 { font-size: 1.5rem; font-weight: 700; color: var(--primary-color); }
                .superadmin-center .card { transition: transform 0.2s; }
                .superadmin-center .card:hover { transform: translateY(-2px); }
            `}</style>
        </div>
    );
};

export default AdminLogs;
